import Dexie, { type Table } from 'dexie'
import type {
  Bristol,
  DailyLog,
  Ingredient,
  Meal,
  MetaEntry,
  Recipe,
  Sensation,
  Stool,
} from '../domain/types'

/**
 * Base locale (IndexedDB). Aucune donnee ne quitte l'appareil.
 *
 * ## Pourquoi aucun booleen n'est indexe
 *
 * IndexedDB n'accepte comme cle que : number, string, Date, ArrayBuffer et Array.
 * Un booleen (comme `perso`, `favorite`, `archivee`) ou un `null` (comme `finAt`
 * pour un episode en cours) n'est **pas une cle valide** : Dexie n'echoue pas,
 * il omet simplement l'enregistrement de l'index. Une requete du type
 * `where('favorite').equals(true)` renverrait alors systematiquement zero
 * resultat, silencieusement.
 *
 * Ces champs sont donc filtres en memoire. Le volume le permet largement :
 * quelques centaines d'ingredients et, meme apres des annees de journal,
 * quelques milliers de repas.
 */
export class SuiviSciDb extends Dexie {
  ingredients!: Table<Ingredient, string>
  recipes!: Table<Recipe, string>
  meals!: Table<Meal, string>
  sensations!: Table<Sensation, string>
  stools!: Table<Stool, string>
  dailyLogs!: Table<DailyLog, string>
  meta!: Table<MetaEntry, string>

  constructor() {
    super('suivi-sci')

    this.version(1).stores({
      ingredients: 'id, nom, categorie, niveauFodmap, *famillesFodmap',
      recipes: 'id, nom',
      meals: 'id, at, type',
      symptomEpisodes: 'id, debutAt, type',
      dailyLogs: 'date',
      meta: 'cle',
    })

    // v2 : tout devient evenement ponctuel.
    //
    // Les sensations quittent le modele a duree (qui obligeait a cloturer, et
    // dont l'oubli desactivait silencieusement l'analyse) et les passages aux
    // selles obtiennent leur propre table, seule capable de porter une frequence.
    this.version(2)
      .stores({
        ingredients: 'id, nom, categorie, niveauFodmap, *famillesFodmap',
        recipes: 'id, nom',
        meals: 'id, at, type',
        sensations: 'id, at, type',
        stools: 'id, at',
        dailyLogs: 'date',
        meta: 'cle',
        // Table v1 supprimee apres migration.
        symptomEpisodes: null,
      })
      .upgrade(async (tx) => {
        interface EpisodeV1 {
          id: string
          type: string
          debutAt: string
          finAt: string | null
          intensite: number
          bristol?: Bristol
          notes?: string
          creeLe: string
          modifieLe: string
        }
        const anciens = await tx.table<EpisodeV1, string>('symptomEpisodes').toArray()
        const sensations = tx.table<Sensation, string>('sensations')
        const selles = tx.table<Stool, string>('stools')

        for (const e of anciens) {
          const estSelle =
            e.type === 'diarrhee' || e.type === 'constipation' || e.type === 'urgence'

          if (estSelle) {
            // Le type d'origine renseigne la consistance quand l'echelle de
            // Bristol n'avait pas ete saisie.
            const bristol: Bristol =
              e.bristol ?? (e.type === 'diarrhee' ? 6 : e.type === 'constipation' ? 2 : 4)
            await selles.put({
              id: e.id,
              at: e.debutAt,
              bristol,
              urgence: e.type === 'urgence',
              ...(e.notes ? { notes: e.notes } : {}),
              creeLe: e.creeLe,
              modifieLe: e.modifieLe,
            })
            continue
          }

          // Sensation : on conserve le debut, et la fin comme second evenement
          // si elle avait ete renseignee. Le regroupement reconstituera une
          // poussee equivalente sans qu'aucune donnee ne soit inventee.
          const type = e.type as Sensation['type']
          await sensations.put({
            id: e.id,
            at: e.debutAt,
            type,
            intensite: e.intensite,
            ...(e.notes ? { notes: e.notes } : {}),
            creeLe: e.creeLe,
            modifieLe: e.modifieLe,
          })
          if (e.finAt && e.finAt !== e.debutAt) {
            await sensations.put({
              id: `${e.id}-fin`,
              at: e.finAt,
              type,
              intensite: e.intensite,
              notes: 'Fin d\'un épisode antérieur au passage en évènements ponctuels.',
              creeLe: e.creeLe,
              modifieLe: e.modifieLe,
            })
          }
          if (e.bristol !== undefined) {
            await selles.put({
              id: `${e.id}-selle`,
              at: e.debutAt,
              bristol: e.bristol,
              urgence: false,
              notes: 'Repris depuis un épisode antérieur à la séparation sensations / selles.',
              creeLe: e.creeLe,
              modifieLe: e.modifieLe,
            })
          }
        }
      })
  }
}

export const db = new SuiviSciDb()

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export async function lireMeta<T>(cle: string, defaut: T): Promise<T> {
  const row = await db.meta.get(cle)
  return row === undefined ? defaut : (row.valeur as T)
}

export async function ecrireMeta(cle: string, valeur: unknown): Promise<void> {
  await db.meta.put({ cle, valeur })
}

export const META_SEED_VERSION = 'seedVersion'
export const META_DERNIER_EXPORT = 'dernierExportAt'

// ---------------------------------------------------------------------------
// Identifiants
// ---------------------------------------------------------------------------

/**
 * `crypto.randomUUID()` n'existe que sur les origines securisees. C'est bien le
 * cas en production (HTTPS) et sur localhost, mais pas si l'app est ouverte via
 * l'IP du reseau local en HTTP pendant le developpement — d'ou le repli.
 */
export function nouvelId(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  if (c && typeof c.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16))
    return Array.from(b, (n) => n.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
